import { NodeType } from '@amplify/amp-module';
import { v4 as uuid } from 'uuid';
import { NodeSizes, defaultNodesData, roundDownTo } from '@/constants/builder';

export const isInsideCoords = (x1, y1, x2, y2, x, y) => x >= x1 && x <= x2 && y >= y1 && y <= y2;

const getNodeBounds = (x, y, width, height) => ({
  x1: x,
  x2: x + width,
  y1: y,
  y2: y + height,
});

export const initNodeId = (type) => `${type}-` + uuid();

export const initNodeData = (node) => ({
  ...defaultNodesData.data.node(),
  ...defaultNodesData.data[node.type](),
  ...node,
});

export const initNewNode = (tool) => {
  const { name, pluginId, settings } = tool;

  const node = {
    id: initNodeId(name),
    type: name,
  };

  if (name === NodeType.plugin) {
    node.pluginId = pluginId;
    node.settings = settings;
  }

  return node;
};

export const adjustNodePositionForNodeSize = (centerPosition, size) => ({
  x: centerPosition.x + size.width / 2,
  y: centerPosition.y + size.height / 2,
});

export const getTargetNode = (nodes, dropNode) =>
  nodes.find((node) => {
    const { x, y } = node.position();
    const { width, height } = node.getSize();
    const nodeBounds = roundBoundsDownTo(getNodeBounds(x, y, width, height), roundDownTo);

    return isInsideCoords(nodeBounds.x1, nodeBounds.y1, nodeBounds.x2, nodeBounds.y2, dropNode.x, dropNode.y);
  });

export const getEdgesData = (edges) =>
  edges.map((edge) => ({
    id: edge.id,
    source: edge.getSourceNode().id,
    target: edge.getTargetNode().id,
  }));

export const roundBoundsDownTo = (bounds, to) => {
  for (const key of Object.keys(bounds)) bounds[key] -= bounds[key] % to;

  return bounds;
};

export const roundPositionDownTo = (pos, to) => ({
  x: pos.x - (pos.x % to),
  y: pos.y - (pos.y % to),
});

export const adjustNodePosition = (pos, size, roundDown) =>
  roundPositionDownTo(adjustNodePositionForNodeSize(pos, size), roundDown);

export const hoverNode = (node, hovered) => {
  node?.setData({
    ...node.getData(),
    hovered,
  });
};

export const getValidNodesFromDirection = (nodeType, isChild = false, hasChildren = false) => {
  const validNodeTypes = [];

  if (!nodeType) {
    return [];
  }

  if (isChild) {
    validNodeTypes.push(
      ...Object.keys(defaultNodesData.disallowedChildren).filter(
        (key) =>
          !defaultNodesData.disallowedChildren[key].includes(nodeType) &&
          !defaultNodesData.disallowedChildren[key].includes('all') &&
          ![NodeType.condition].includes(key) // Adding a condition as a parent is disabled until Moving Nodes is implemented
      )
    );
  } else {
    const disallowedChildren = defaultNodesData.disallowedChildren[nodeType];
    const cannotHaveChildren = [NodeType.condition, NodeType.goTo]; // Adding condition Nodes as children when there are already children is disabled until Moving Nodes is implemented

    if (!disallowedChildren.includes('all')) {
      validNodeTypes.push(
        ...Object.values(NodeType).filter(
          (v) => !disallowedChildren.includes(v) && (!hasChildren || !cannotHaveChildren.includes(v))
        )
      );
    }
  }

  return validNodeTypes;
};

export const isValidDrop = (targetNodeType, droppedNodeType, targetHasChildren = false, flippedValidation = false) => {
  let valid = false;

  if (!targetNodeType || !droppedNodeType) {
    return false;
  }

  if (flippedValidation) {
    // This happens if adding new First Node
    const disallowed = defaultNodesData.disallowedChildren[droppedNodeType];

    if (disallowed && ![NodeType.condition, NodeType.goTo].includes(droppedNodeType)) {
      valid = !disallowed.includes(targetNodeType) && !disallowed.includes('all');
    }
  } else {
    const disallowed = defaultNodesData.disallowedChildren[targetNodeType];

    if (disallowed) {
      valid = !disallowed.includes(droppedNodeType) && !disallowed.includes('all');
    }

    if (valid && [NodeType.condition, NodeType.goTo].includes(droppedNodeType)) {
      valid = !targetHasChildren;
    }
  }
  return valid;
};

export const toggleNodeSelection = (node, data, selected) => {
  node.setData({
    ...node.getData(),
    selected,
  });

  const { width, height } = node.size();

  if (
    node.shape === 'open-node' ||
    node.shape === 'player-node' ||
    node.shape === 'message-node' ||
    node.shape === 'goTo-node'
  ) {
    node.resize(width, selected ? height + 28 : height - 28);
  } else if (node.shape === 'condition-node') {
    let heightAddOn = 10;

    if (data.storageKey) {
      heightAddOn += 34;
    }

    if (data.condition) {
      heightAddOn += 32;
    }

    node.resize(width, selected ? height + heightAddOn : height - heightAddOn);
  }
};

export const parseToEdges = (nodes) => {
  return (
    nodes?.reduce((edges, node) => {
      if (!node || !node?.parentId) {
        return edges;
      }

      const { id, parentId } = node;
      const source = parentId.includes('port')
        ? // in this case the parentId is the actual port id
          { cell: parentId.split('_')[0], port: parentId }
        : { cell: parentId };

      return [...edges, { shape: 'edge', source, target: { cell: id } }];
    }, []) || []
  );
};

export const cleanChangeSet = (changeSet) => {
  const cleanChanges = [];

  changeSet.forEach((change) => {
    if (!change.key.includes('-1')) {
      cleanChanges.push(change);
    }
  });

  return cleanChanges;
};

export const mendTreeAroundNode = (nodes, data) => {
  const child = Object.values(nodes).find((n) => n.parentId && n.parentId.includes(data.id));
  if (!child) {
    return;
  }

  data.parentId ? (child.parentId = data.parentId) : delete child.parentId;
};

// Note: Condition Nodes are not supported to be inserted into the flow before it is possible to Move Nodes in the canvas
export const insertNodeIntoTree = (nodes, data) => {
  const existingChild = Object.values(nodes).find((n) => n.parentId === data.parentId);
  if (existingChild) {
    if (![NodeType.buttonGroup, NodeType.question].includes(data.type)) existingChild.parentId = data.id;
    else {
      const subNodes = Object.values(data.subNodes);
      existingChild.parentId = `${data.id}_port_${subNodes[0].id}`;
    }
  }
};

export const isButtonMultiline = (buttonTexts = []) => {
  const nbOfButtons = buttonTexts.length;
  // use placeholder to calculate the number of characters when text is empty
  const nbOfChars = buttonTexts.map((text) => text || 'Add button text').join('').length;

  return (nbOfButtons > 1 && nbOfChars > 15) || (nbOfButtons === 1 && nbOfChars > 28);
};

const getCardIncreaseHeight = (data) => {
  const { email, link, media } = data;
  let height = 0;

  if (link.title && link.url) {
    height += 17;
  }

  if (email.address && email.title) {
    height += 1;
  }

  if (media.url) {
    height += 48;
  }

  return height;
};

export const getCarouselIncreaseSize = (subNodes) => {
  const subNodesHeights = subNodes.map((subNode) => getCardIncreaseHeight(subNode));

  return Math.max(...subNodesHeights);
};

export const getNodeActualSize = (node, data) => {
  const nodeType = node.shape.split('-')[0];
  if (!NodeSizes[nodeType]) {
    console.warn('No node registered with type of', nodeType, node);
    return { width: 0, height: 0 };
  }
  const { width, height, subNodeWidth } = NodeSizes[nodeType] || {};

  const getGroupWidth = () => {
    const subNodesLength = Object.keys(data?.subNodes || {}).length;

    // Adds up all the subnodes lenghts + spacing between them.
    let groupWidth = subNodesLength * subNodeWidth + (subNodesLength + 1) * 16;

    if (nodeType === NodeType.question && subNodesLength > 1) {
      groupWidth += 32;
    }

    return groupWidth;
  };

  switch (nodeType) {
    case NodeType.buttonGroup: {
      const subNodesArray = Object.values(data?.subNodes || {});
      const hasMultilineContent = subNodesArray.some((subNode) => isButtonMultiline(subNode.buttonTexts));
      let finalHeight = height;

      if (hasMultilineContent) {
        finalHeight += 28;
      }

      return { width: getGroupWidth(), height: finalHeight };
    }

    case NodeType.carousel: {
      const subNodesArray = Object.values(data?.subNodes || {});
      const finalHeight = height + getCarouselIncreaseSize(subNodesArray);

      return { width: getGroupWidth(), height: finalHeight };
    }

    case NodeType.player: {
      const { media } = data;
      let finalHeight = height;

      if (media.url) {
        finalHeight += 51;
      }

      return { width, height: finalHeight };
    }

    case NodeType.plugin: {
      const showSettings = !!Object.values(data?.settings || {}).find((value) => !!value);
      let finalHeight = height;

      if (showSettings) {
        const nbOfFields = Object.values(data?.settings || {}).length;

        if (nbOfFields > 1) {
          finalHeight += 24 * (nbOfFields - 1);
        }
      }

      return { width, height: finalHeight };
    }

    case NodeType.question: {
      const subNodesArray = Object.values(data?.subNodes || {});
      const hasMultilineContent = subNodesArray.some((subNode) => isButtonMultiline(subNode.buttonTexts));
      let finalHeight = height;

      if (hasMultilineContent) {
        finalHeight += 28;
      }

      if (subNodesArray.length > 1) {
        finalHeight += 32;
      }

      return { width: getGroupWidth(), height: finalHeight };
    }

    case NodeType.condition:
    case NodeType.goTo:
    case NodeType.message:
    default:
      return { width, height };
  }
};

export const buttonsToValues = (subNodes) => {
  return Object.values(subNodes).reduce((prev, current) => {
    prev = [...prev, ...current.buttonTexts];
    return prev;
  }, []);
};
